import React, { useCallback, useMemo } from 'react';
import { getInlineString, getText } from 'i18n/intl';
import { mapCol2 } from 'pages/configuration/fields/sections/fieldUtils';
import type { BatchEditOperation } from 'types/batchEditObject.type';
import type { TField } from '@timeedit/types';
import { Alert, Icon, Segmented, Tooltip } from '@timeedit/ui-components';
import {
  Button,
  Flex,
  Form,
  type FormInstance,
  type SelectProps,
  type TreeSelectProps,
} from '@timeedit/ui-components/antd';
import type { Rule } from '@timeedit/ui-components/antd/form/index';
import { JobObjectSelection, useJobSubmit } from '../../hooks/useJobSubmit';
import {
  useReuseJobMutation,
  useStartJobMutation,
} from '../JobResults/api/objectJobApi';
import './BulkEditField.scss';

export type BulkEditFieldType = Pick<
  TField,
  | 'id'
  | 'name'
  | 'description'
  | 'multiple'
  | 'editable'
  | 'fieldType'
  | 'categories'
  | 'min'
  | 'max'
  | 'unique'
  | 'mandatory'
> & {
  treeData?: TreeSelectProps['treeData'];
  length?: TField['length'];
  disabledCategories?: string[];
  filter?: string;
};

export type BulkEditTreeSelectFieldType = Pick<
  TField,
  | 'id'
  | 'name'
  | 'description'
  | 'multiple'
  | 'editable'
  | 'min'
  | 'max'
  | 'unique'
  | 'mandatory'
> & {
  treeData?: TreeSelectProps['treeData'];
};

export type CommonProps = {
  field: BulkEditFieldType;
  form: FormInstance<any>;
};

export type CommonTreeSelectProps = {
  field: BulkEditTreeSelectFieldType;
  form: FormInstance<any>;
};

type BulkEditSelectField = Pick<BulkEditFieldType, 'editable' | 'multiple'>;

export function getBulkEditSelectProps(
  field: BulkEditSelectField
): Pick<SelectProps, 'maxCount' | 'mode'> {
  if (!field.editable && !field.multiple) {
    return {};
  }
  if (field.editable && !field.multiple) {
    return {
      maxCount: 1,
      mode: 'tags',
    };
  }
  if (!field.editable && field.multiple) {
    return {
      mode: 'multiple',
    };
  }
  return {
    mode: 'tags',
  };
}

export function getBulkEditTreeSelectProps(
  field: BulkEditSelectField
): Pick<TreeSelectProps, 'treeCheckable'> {
  if (!field.multiple) {
    return {};
  }

  return {
    treeCheckable: field.editable ? undefined : true,
  };
}

type BulkEditSubmitParams = {
  form: FormInstance<any>;
  setSendingBatch: (sending: boolean) => void;
  setOpen: (openSection: string | false) => void;
  selection: JobObjectSelection;
  operationType: string;
  buildOperations: (values: Record<string, any>) => BatchEditOperation[];
  successMessage: string;
  onSuccess?: () => void;
};

export const useBulkEditSubmit = ({
  form,
  setSendingBatch,
  setOpen,
  selection,
  operationType,
  buildOperations,
  successMessage,
  onSuccess,
}: BulkEditSubmitParams) => {
  const [startJob] = useStartJobMutation();
  const [reuseJob] = useReuseJobMutation();

  const getFetchArgs = useCallback(async () => {
    const values = await form.validateFields();
    return {
      operationType,
      operations: buildOperations(values),
    };
  }, [form, operationType, buildOperations]);

  return useJobSubmit({
    form,
    setSendingBatch,
    setOpen,
    selection,
    successMessage,
    onSuccess,
    startJob,
    reuseJob,
    getFetchArgs,
  });
};

const ACTIONS = {
  keep: 'keep',
  replaceAll: 'replaceAll',
  findReplace: 'findReplace',
  add: 'add',
  removeSpecific: 'removeSpecific',
  removeAll: 'removeAll',
};

export type BulkEditAction = keyof typeof ACTIONS;

const OPTIONS = [
  {
    value: 'keep',
    label: <>{getText('object_manager.bulk-edit-modal.keep.label')}</>,
  },

  {
    value: 'add',
    label: <>{getText('object_manager.bulk-edit-modal.add.label')}</>,
  },
  {
    value: 'findReplace',
    label: <>{getText('object_manager.bulk-edit-modal.findReplace.label')}</>,
  },
  {
    value: 'replaceAll',
    label: <>{getText('object_manager.bulk-edit-modal.replaceAll.label')}</>,
  },
  {
    value: 'removeSpecific',
    label: (
      <>{getText('object_manager.bulk-edit-modal.removeSpecific.label')}</>
    ),
  },
  {
    value: 'removeAll',
    label: <>{getText('object_manager.bulk-edit-modal.removeAll.label')}</>,
  },
] as const;

export const getBulkEditFieldOptions = (
  includedActions: BulkEditAction[],
  field: Pick<BulkEditFieldType, 'multiple' | 'unique' | 'mandatory'>,
  disableActions: BulkEditAction[] = []
) => {
  if (field.unique) {
    return OPTIONS.filter(
      (option) =>
        option.value === 'keep' ||
        (option.value === 'removeAll' &&
          !field.mandatory &&
          !disableActions.includes('removeAll'))
    );
  }

  return OPTIONS.filter(
    (option) =>
      includedActions.includes(option.value as keyof typeof ACTIONS) &&
      (option.value !== 'add' || field.multiple)
  ).map((option) => {
    const disabled = disableActions.includes(option.value);
    return {
      ...option,
      disabled,
      label: (
        <Tooltip
          title={
            disabled
              ? getText('object_manager.bulk-edit-modal.tooltip.no-categories')
              : undefined
          }
        >
          {option.label}
        </Tooltip>
      ),
    };
  });
};

export const getIntegerRangeErrorMessage = (min?: number, max?: number) => {
  if (min != null && max != null) {
    return `Value must be between ${min} and ${max}`;
  }
  if (min != null) {
    return `Value must be at least ${min}`;
  }
  if (max != null) {
    return `Value must be at most ${max}`;
  }
  return 'Invalid integer value';
};

export const BulkEditFormEditValue = ({
  name,
  input,
  allowMultiple = false,
  rules,
  label,
}: {
  name: string;
  allowMultiple?: boolean;
  input: React.ReactNode;
  rules?: Rule[];
  label?: string;
}) => {
  return (
    <Form.List initialValue={[undefined]} name={name}>
      {(fields, { add, remove }) => (
        <div className="bulk-edit-input-wrapper">
          {fields.map(({ key, ...field }, i) => (
            <React.Fragment key={key}>
              <div className="bulk-edit-list-input-wrapper">
                <Form.Item
                  className={`bulk-edit-full-width${
                    label && i > 0 ? ' bulk-edit-hidden-label' : ''
                  }`}
                  required
                  rules={rules}
                  label={label || undefined}
                  {...field}
                >
                  {input}
                </Form.Item>
                {allowMultiple && (
                  <Button
                    type="text"
                    size="small"
                    style={{ border: '1px solid #00000073' }}
                    icon={<Icon id="DeleteOutlined" alt="Delete" />}
                    disabled={i === 0 && fields.length === 1}
                    onClick={() => remove(field.name)}
                  />
                )}
              </div>
            </React.Fragment>
          ))}
          {allowMultiple && (
            <Form.Item
              className={label ? 'bulk-edit-hidden-label' : undefined}
              label={label || undefined}
            >
              <Button
                className="bulk-edit-add-button"
                size="small"
                type="dashed"
                onClick={() => add(undefined)}
              >
                Add
              </Button>
            </Form.Item>
          )}
        </div>
      )}
    </Form.List>
  );
};

export const BulkEditMatchRule = ({
  fieldId,
  isMultiple = false,
}: {
  fieldId: number | string;
  isMultiple?: boolean;
}) => {
  if (!isMultiple) {
    return null;
  }
  return (
    <Form.Item
      name={`${fieldId}_rule`}
      initialValue="matchAll"
      label={getText('object_manager.bulk-edit-modal.match-rule.label')}
      tooltip={getText('object_manager.bulk-edit-modal.match-rule.tooltip')}
    >
      <Segmented
        size="small"
        options={[
          {
            value: 'matchAll',
            label: getText(
              'object_manager.bulk-edit-modal.match-rule.match-all'
            ),
          },
          {
            value: 'matchOne',
            label: getText(
              'object_manager.bulk-edit-modal.match-rule.match-any'
            ),
          },
        ]}
      />
    </Form.Item>
  );
};

export const useBulkEditWarnings = (
  form: FormInstance,
  idNameMap: Record<string, { name: string }>,
  selectedCount: number
) => {
  const values = Form.useWatch([], form);

  const warnings = useMemo(() => {
    const tempWarnings: React.ReactNode[] = [];

    Object.entries(values ?? {})?.forEach(([key, value]) => {
      if (value === 'removeAll') {
        const fieldName = idNameMap[key]?.name ?? key;
        tempWarnings.push(
          <Alert
            key={key}
            title={getInlineString(
              'object_manager.bulk-edit-modal.warning.clear',
              fieldName,
              selectedCount
            )}
            type="warning"
            showIcon
          />
        );
      } else if (value === 'replaceAll') {
        const fieldName = idNameMap[key]?.name ?? key;
        tempWarnings.push(
          <Alert
            key={key}
            title={getInlineString(
              'object_manager.bulk-edit-modal.warning.replace-all',
              fieldName,
              selectedCount
            )}
            type="warning"
            showIcon
          />
        );
      }
    });
    return tempWarnings;
  }, [form, idNameMap, selectedCount, values]);

  if (warnings.length === 0) {
    return null;
  }

  return (
    <Flex className="bulk-edit-warning-container" vertical gap="small">
      {warnings}
    </Flex>
  );
};

export const useBulkEditHasSelection = (form: FormInstance) => {
  const values = Form.useWatch([], form);

  return useMemo(
    () =>
      Object.values(values ?? {}).filter(
        (value) => value !== 'keep' && value != null
      ).length > 0,
    [values]
  );
};

export const useBulkEditFieldDisabled = (field: BulkEditFieldType) => {
  return useMemo(() => {
    const config = mapCol2[field.fieldType];
    const canBeEditable = config?.editable?.isEnabled;

    if (config && !canBeEditable) {
      return {
        disabled: !config.editable?.defaultValue,
        tooltip: field.description,
      };
    }

    if (!field.editable) {
      return {
        disabled: true,
        tooltip: getText(
          'object_manager.bulk-edit-modal.tooltip.field-not-editable'
        ),
      };
    }

    return {
      disabled: false,
      tooltip: field.description,
    };
  }, [field.editable, field.description, field.fieldType]);
};
